import React from 'react';

import { MANIFEST_FALLBACK, useManifestQuery } from '@local/api';

import { BASE_CHOWNOW_URL, BASE_SUPPORT_URL, CHOWNOW_URL } from 'helpers/paths';

import { ButtonLink } from 'primitives/ButtonLink';
import { Outlink } from '@local/outlink';

import usePlatform from 'hooks/usePlatform';
import { useIsOneTrustInitialized } from '@local/third-party-offers';

import { PoweredByBadge } from 'components/PoweredByBadge';
import { ReactComponent as IconSocialInstagram } from 'images/Icon-SocialInstagram.svg';
import { ReactComponent as IconSocialFacebook } from 'images/Icon-SocialFacebook.svg';
import { ReactComponent as IconSocialTwitter } from 'images/Icon-SocialTwitter.svg';

import styles from './styles.module.scss';

// eslint-disable-next-line complexity
export default function Footer() {
  const { isMarketplacePlatform } = usePlatform();

  const isOneTrustReady = useIsOneTrustInitialized();
  const showCookieSettings =
    isOneTrustReady && !!window.OneTrust?.GetDomainData?.()?.ShowAlertNotice;

  const { data } = useManifestQuery();

  const poweredOutlink = (
    <Outlink to={CHOWNOW_URL} ariaLabel="Powered by ChowNow">
      <PoweredByBadge />
    </Outlink>
  );

  return (
    <footer className={styles.footer} data-testid="Footer">
      <div className={styles.footerContent}>
        {!isMarketplacePlatform && (
          <div className={styles.logoWrapper}>{poweredOutlink}</div>
        )}
        <div className={styles.textOutlinks}>
          <Outlink ariaLabel="About Us" to={`${BASE_CHOWNOW_URL}/about`}>
            About Us
          </Outlink>
          <Outlink ariaLabel="Help" to={`${BASE_SUPPORT_URL}/customer/s/`}>
            Help
          </Outlink>
          <Outlink
            ariaLabel="Terms of Use"
            to={
              data?.terms_of_service_url ??
              MANIFEST_FALLBACK.terms_of_service_url
            }
          >
            Terms of Use
          </Outlink>
          <Outlink
            ariaLabel="Privacy Policy"
            to={
              data?.privacy_policy_url ?? MANIFEST_FALLBACK.privacy_policy_url
            }
          >
            Privacy Policy
          </Outlink>
          <Outlink
            ariaLabel="Do Not Sell or Share My Personal Information"
            to={data?.ccpa_url ?? MANIFEST_FALLBACK.ccpa_url}
          >
            Do Not Sell or Share My Personal Information
          </Outlink>
          <Outlink
            ariaLabel="Limit The Use Of My Sensitive Personal Information"
            to={data?.ccpa_url ?? MANIFEST_FALLBACK.ccpa_url}
          >
            Limit The Use Of My Sensitive Personal Information
          </Outlink>
          {showCookieSettings && (
            <ButtonLink
              onClick={() => {
                window.Optanon?.ToggleInfoDisplay();
              }}
              size="xsmall"
              underline={false}
            >
              Cookie Settings
            </ButtonLink>
          )}
        </div>
        {isMarketplacePlatform ? (
          <div className={styles.socialLinks}>
            <Outlink
              ariaLabel="Instagram"
              className={styles.socialIcon}
              to="https://instagram.com/ChowNow/"
            >
              <IconSocialInstagram />
            </Outlink>
            <Outlink
              ariaLabel="Twitter"
              className={styles.socialIcon}
              to="https://twitter.com/ChowNow/"
            >
              <IconSocialTwitter />
            </Outlink>
            <Outlink
              ariaLabel="Facebook"
              className={styles.socialIcon}
              to="https://facebook.com/ChowNow/"
            >
              <IconSocialFacebook />
            </Outlink>
          </div>
        ) : (
          <div className={styles.mobileLogo}>{poweredOutlink}</div>
        )}
      </div>
    </footer>
  );
}
